// src/components/MainLayout.js
import React from "react";
import Modal from "./Modal";
import LoginModal from "./LoginModal";
import CategoryMenu from "./CategoryMenu";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import NotificationsDropdown from "./NotificationsDropdown";
import axios from "../axios";
import { getUnreadChatCount } from "../api/chatApi";
import { WS_BASE_URL } from "../config";
import UserMenu from "./UserMenu";
import stoaLogo from "../assets/stoa-logo.png";

const getColorFromName = (name = "") => {
  const colors = ["#ffadad", "#ffd6a5", "#caffbf", "#9bf6ff", "#bdb2ff", "#ffc6ff"];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const MainLayout = ({
  children,
  user,
  setUser,
  token,
  setToken,
  showLoginModal,
  setShowLoginModal,
  showDropdown,
  setShowDropdown,
  handleAvatarChange,
  handleLogout,
  categories,
  searchTerm,
  setSearchTerm,
  fetchAds,
  popularSubcategories,
  onPickCategory,
  onProfileClick,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  const chatUpdatesSocketRef = useRef(null);
  const chatUpdatesReconnectTimerRef = useRef(null);
  const chatUpdatesReconnectAttemptRef = useRef(0);
  const shouldReconnectChatUpdatesRef = useRef(true);
  const [showNotifDropdown, setShowNotifDropdown] = React.useState(false);
  const [notificationUnreadCount, setNotificationUnreadCount] = React.useState(0);
  const [chatUnreadCount, setChatUnreadCount] = React.useState(0);
  const [showAuthPopover, setShowAuthPopover] = React.useState(false);
  const [isStandaloneApp, setIsStandaloneApp] = React.useState(false);
  const [isMobileView, setIsMobileView] = React.useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const currentProfileId = user?.profile?.id;

  useEffect(() => {
    const loadUnread = async () => {
      const t = localStorage.getItem("token");

      if (!t) {
        setNotificationUnreadCount(0);
        setChatUnreadCount(0);
        return;
      }

      try {
        const [notifRes, chatRes] = await Promise.all([
          axios.get("/api/notifications/unread-count/"),
          getUnreadChatCount(t),
        ]);

        const notifCnt = Number(notifRes.data?.unread_count ?? 0);
        const chatCnt = Number(chatRes?.unread_conversations ?? 0);

        setNotificationUnreadCount(Number.isFinite(notifCnt) ? notifCnt : 0);
        setChatUnreadCount(Number.isFinite(chatCnt) ? chatCnt : 0);
      } catch (e) {
        setNotificationUnreadCount(0);
        setChatUnreadCount(0);
      }
    };

    loadUnread();

    const handler = () => loadUnread();
    window.addEventListener("refresh-unread-count", handler);

    return () => window.removeEventListener("refresh-unread-count", handler);
  }, [user, token]);

  useEffect(() => {
    if (!token || !currentProfileId) return;

    shouldReconnectChatUpdatesRef.current = true;
    chatUpdatesReconnectAttemptRef.current = 0;

    const reconnectDelays = [1000, 2000, 5000, 10000, 15000];

    const connectChatUpdatesWebSocket = () => {
      if (!shouldReconnectChatUpdatesRef.current) return;

      const wsUrl = `${WS_BASE_URL}/ws/chat/updates/?token=${token}`;
      const ws = new WebSocket(wsUrl);

      chatUpdatesSocketRef.current = ws;

      ws.onopen = () => {
        console.log("✅ Global chat updates WebSocket открыт:", wsUrl);
        chatUpdatesReconnectAttemptRef.current = 0;

        window.dispatchEvent(new Event("refresh-unread-count"));
        window.dispatchEvent(new Event("refresh-chat-list"));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "conversation.updated") {
            const lastMessage = data.last_message;
            const senderId = data.sender_id ?? lastMessage?.sender_id;

            const isIncomingMessage =
              String(senderId) !== String(currentProfileId);

            if (
              isIncomingMessage &&
              lastMessage?.id &&
              !lastMessage.delivered_at &&
              ws.readyState === WebSocket.OPEN
            ) {
              ws.send(
                JSON.stringify({
                  type: "message.delivered",
                  conversation_id: data.conversation_id,
                  message_ids: [lastMessage.id],
                })
              );
            }

            window.dispatchEvent(new Event("refresh-unread-count"));
            window.dispatchEvent(new Event("refresh-chat-list"));
          }

          if (data.type === "message.status_updated") {
            window.dispatchEvent(
              new CustomEvent("chat-message-status-updated", {
                detail: data,
              })
            );
          }
          if (data.type === "profile.presence_updated") {
            window.dispatchEvent(
              new CustomEvent("profile-presence-updated", {
                detail: data,
              })
            );
          }
        } catch (err) {
        console.error("Ошибка global chat updates WebSocket:", err);
        }
      };

      ws.onerror = (event) => {
        console.error("❌ Global chat updates WebSocket ошибка:", event);
      };

      ws.onclose = (event) => {
        console.log("❌ Global chat updates WebSocket закрыт:", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          attempt: chatUpdatesReconnectAttemptRef.current + 1,
        });

        if (chatUpdatesSocketRef.current === ws) {
          chatUpdatesSocketRef.current = null;
        }

        if (!shouldReconnectChatUpdatesRef.current) return;

        const delay =
          reconnectDelays[
            Math.min(
              chatUpdatesReconnectAttemptRef.current,
              reconnectDelays.length - 1
            )
          ];

        chatUpdatesReconnectAttemptRef.current += 1;

        chatUpdatesReconnectTimerRef.current = setTimeout(() => {
          connectChatUpdatesWebSocket();
        }, delay);
      };
    };

    connectChatUpdatesWebSocket();

    return () => {
      shouldReconnectChatUpdatesRef.current = false;

      if (chatUpdatesReconnectTimerRef.current) {
        clearTimeout(chatUpdatesReconnectTimerRef.current);
        chatUpdatesReconnectTimerRef.current = null;
      }

      if (chatUpdatesSocketRef.current) {
        chatUpdatesSocketRef.current.close();
        chatUpdatesSocketRef.current = null;
      }
    };
  }, [token, currentProfileId]);

  useEffect(() => {
    function handleClickOutside(event) {
      // Если кликнули вне меню и вне кнопки меню — закрываем
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest(".menu-button")
      ) {
        setShowDropdown(false);
      }
    }

    // Подключаем слушатель только когда меню открыто
    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Чистим слушатель при размонтировании
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown, setShowDropdown]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        // не закрываем если клик по самой кнопке колокольчика
        if (!event.target.closest(".notif-button")) {
          setShowNotifDropdown(false);
        }
      }
    }

    if (showNotifDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showNotifDropdown]);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    setIsStandaloneApp(standalone);
  }, []);

  useEffect(() => {
    const updateIsMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    updateIsMobileView();
    window.addEventListener("resize", updateIsMobileView);

    return () => {
      window.removeEventListener("resize", updateIsMobileView);
    };
  }, []);

  const displayName =
    (user?.profile?.name && user.profile.name.trim()) ||
    user?.email ||
    user?.profile?.phone ||
    user?.username ||
    "";
  const displayInitial = (displayName || "?").charAt(0).toUpperCase();

  const closeUserMenu = () => setShowDropdown(false);

  const openMyAds = () => navigate("/my-ads");

  const openProfile = () => {
    onProfileClick && onProfileClick();
  };
  

  return (
    <>
      <div className="container">
        <div className="top-bar">
          <button
            type="button"
            className="brand-logo-button"
            onClick={() => navigate("/")}
            aria-label="STOA — αρχική σελίδα"
          >
            <img
              src={stoaLogo}
              alt="STOA"
              className="brand-logo"
            />
          </button>

          <div className="auth-panel">
            {!user ? (
              <>
                <div style={{ position: "relative" }}>
                  <button
                    className="icon-button notif-button"
                    style={{ marginRight: "10px", marginTop: "6px" }}
                    title="Ειδοποιήσεις"
                    onClick={() => {
                      // гость → popover
                      setShowAuthPopover((v) => !v);
                      setShowLoginModal(false);
                    }}
                  >
                    📢
                  </button>

                  {showAuthPopover && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 8px)",
                        zIndex: 10000,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        boxShadow: "0 12px 30px rgba(0,0,0,.14)",
                        padding: 12,
                        width: 260,
                      }}
                    >
                      <div style={{ fontWeight: 700, marginBottom: 6 }}>
                        Ειδοποιήσεις
                      </div>
                      <div style={{ fontSize: 14, color: "#6b7280", marginBottom: 10 }}>
                        Για να δείτε ειδοποιήσεις, πρέπει να συνδεθείτε.
                      </div>
                      <button
                        className="green-button"
                        style={{ width: "100%" }}
                        onClick={() => {
                          setShowAuthPopover(false);
                          setShowLoginModal(true);
                        }}
                      >
                        Είσοδος ή Εγγραφή
                      </button>
                    </div>
                  )}
                </div>
                {/* ❤️ КНОПКА ИЗБРАННОГО ДЛЯ ГОСТЯ */}
                <button
                  className="icon-button favorite-header-btn"
                  style={{ marginRight: "10px", marginTop: "6px" }}
                  onClick={() => {
                    if (!token) {
                      setShowLoginModal(true);   // если не авторизован — просим войти
                    } else {
                      navigate("/favorites");    // если вдруг токен есть — откроем избранное
                    }
                  }}
                  title="Αγαπημένες αγγελίες"
                >
                   <svg
                      className="header-heart-icon"
                      viewBox="-2 -2 28 28"
                      aria-hidden="true"
                    >
                      <path
                        d="M12 22 C12 22 3 14.2 3 8.5 C3 5 5.6 2.5 9 2.5 C11 2.5 12.7 3.6 13.7 5.2 C14.7 3.6 16.4 2.5 18.4 2.5 C21.8 2.5 24 5 24 8.5 C24 14.2 15 22 15 22 C14 23 13 23 12 22 Z"
                      />
                    </svg>
                </button>

                <button className="green-button" onClick={() => setShowLoginModal(true)}>
                  Είσοδος ή Εγγραφή
                </button>
                <button
                  className="green-button"
                  style={{ marginLeft: "10px" }}
                  onClick={() => {
                    if (!token) {
                      setShowLoginModal(true);
                    } else {
                      navigate("/create");
                    }
                  }}
                >
                  Δημιουργία αγγελίας
                </button>
              </>
            ) : (
              <>
                <div
                  className="auth-block"
                  onClick={onProfileClick}
                  style={{ cursor: "pointer" }}
                >
                  <div className="avatar-wrapper">
                    {user?.profile?.avatar ? (
                      <img
                        src={`${user.profile.avatar}?${new Date().getTime()}`}
                        alt="avatar"
                        className="avatar"
                      />
                    ) : (
                      <div
                        className="placeholder-avatar"
                        style={{
                          backgroundColor: getColorFromName(displayName || ""),
                        }}
                      >
                        {displayInitial}
                      </div>
                    )}
                  </div>
                  <span className="username">{displayName}</span>
                </div>
                <div style={{ position: "relative" }}>
                  <button
                    className="icon-button notif-button"
                    style={{ marginLeft: "10px", marginTop: "6px", position: "relative" }}
                    title="Ειδοποιήσεις"
                    onClick={() => {
                      setShowAuthPopover(false);

                      setShowNotifDropdown((prev) => {
                        const next = !prev;

                        // ✅ открываем dropdown
                        if (next) {
                          // 1) моментально убираем цифру в UI
                          setNotificationUnreadCount(0);

                          // 2) сохраняем "seen" на сервере, чтобы после refresh не возвращалась
                          axios
                            .post("/api/notifications/mark-seen/")
                            .catch((e) => console.warn("mark-seen failed:", e));
                        }

                        return next;
                      });
                    }}
                  >
                    📢
                    {notificationUnreadCount > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          background: "#ef4444",
                          color: "#fff",
                          borderRadius: 999,
                          fontSize: 12,
                          padding: "2px 6px",
                          lineHeight: 1.2,
                          fontWeight: 700,
                        }}
                      >
                        {notificationUnreadCount > 99 ? "99+" : notificationUnreadCount}
                      </span>
                    )}
                  </button>

                  {showNotifDropdown && (
                    <div
                      ref={notifRef}
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 8px)",
                        zIndex: 10000,
                      }}
                    >
                      <NotificationsDropdown
                        onClose={() => setShowNotifDropdown(false)}
                        onOpenAll={() => navigate("/notifications")}
                      />
                    </div>
                  )}
                </div>

                {/* ❤️ КНОПКА ИЗБΡΑΝΝΟГО ДЛЯ АВТОРИЗОВАННОГО */}
                <button
                  className="icon-button favorite-header-btn"
                  style={{ marginLeft: "10px", marginTop: "6px" }}
                  onClick={() => navigate("/favorites")}
                  title="Αγαπημένες αγγελίες"
                >
                  <svg
                    className="header-heart-icon"
                    viewBox="-2 -2 28 28"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 22 C12 22 3 14.2 3 8.5 C3 5 5.6 2.5 9 2.5 C11 2.5 12.7 3.6 13.7 5.2 C14.7 3.6 16.4 2.5 18.4 2.5 C21.8 2.5 24 5 24 8.5 C24 14.2 15 22 15 22 C14 23 13 23 12 22 Z"
                    />
                  </svg>
                </button>
                <button
                  className="green-button"
                  style={{ marginLeft: "10px" }}
                  onClick={() => navigate("/create")}
                >
                  Δημιουργία αγγελίας
                </button>
                {!isStandaloneApp && (
                  <button
                    className="gray-button"
                    style={{
                      marginLeft: "10px",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      position: "relative",
                    }}
                    onClick={() => {
                      window.open("/chat", "_blank", "noopener,noreferrer");
                    }}
                  >
                    💬 <span>Μηνύματα</span>

                    {chatUnreadCount > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          background: "#ef4444",
                          color: "#fff",
                          borderRadius: 999,
                          fontSize: 12,
                          padding: "2px 6px",
                          lineHeight: 1.2,
                          fontWeight: 700,
                        }}
                      >
                        {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                      </span>
                    )}
                  </button>
                )}
                {/* НОВОЕ: отдельная кнопка «Μενού» + выпадающее меню под ней */}
                <div
                  className="menu-wrapper"
                  style={{ marginLeft: "10px", position: "relative" }} // ← добавили position:relative
                >
                  <button
                    className="menu-button"
                    onClick={() => setShowDropdown((v) => !v)}
                    aria-expanded={showDropdown ? "true" : "false"}
                    aria-haspopup="menu"
                  >
                    ☰ Μενού
                  </button>

                  {showDropdown && !isMobileView && (
                    <div
                      ref={menuRef}
                      className="dropdown-menu"
                      role="menu"
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 8px)",
                        zIndex: 10000,
                        background: "#fff",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                        minWidth: 220,
                        padding: 8,
                      }}
                    >
                      <UserMenu
                        onClose={closeUserMenu}
                        onOpenMyAds={openMyAds}
                        onOpenProfile={openProfile}
                        onChangeAvatar={handleAvatarChange}
                        onLogout={handleLogout}
                        avatarInputId="avatar-upload-desktop"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="search-and-menu">
          <CategoryMenu categories={categories} onPickCategory={onPickCategory} />
          <div className="search-bar-combined">
            <input
              className="search-bar-input"
              type="text"
              placeholder="Αναζήτηση..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button onClick={fetchAds} className="search-bar-button">
              Αναζήτηση
            </button>
          </div>
        </div>

        {location.pathname === "/" && (
          <div className="quick-links">
            {popularSubcategories.map((subCategory) => (
              <button
                key={subCategory.id}
                type="button"
                className="quick-link"
                onClick={() => onPickCategory?.(subCategory.id)}
              >
                {subCategory.name}
              </button>
            ))}
          </div>
        )}

        {/* 👉 сюда попадает содержимое каждой страницы */}
        {children}
      </div>
      {/* 📱 Bottom Navigation (только мобилка) */}
      <div className="bottom-nav">
        <button onClick={() => navigate("/")}>🔍</button>

        <button onClick={() => navigate("/favorites")}>
          ❤️
        </button>

        <button
          onClick={() => {
            if (!token) {
              setShowLoginModal(true);
            } else {
              navigate("/create");
            }
          }}
          className="bottom-create-btn"
        >
          ＋
        </button>

        <button
          onClick={() => {
            if (!token) {
              setShowLoginModal(true);
            } else {
              navigate("/notifications");
            }
          }}
          style={{ position: "relative" }}
        >
          📢
          {notificationUnreadCount + chatUnreadCount > 0 && (
            <span
              style={{
                position: "absolute",
                top: -4,
                right: -4,
                background: "#ef4444",
                color: "#fff",
                borderRadius: 999,
                fontSize: 10,
                minWidth: 16,
                height: 16,
                padding: "0 4px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
                fontWeight: 700,
              }}
            >
              {notificationUnreadCount + chatUnreadCount > 99
                ? "99+"
                : notificationUnreadCount + chatUnreadCount}
            </span>
          )}
        </button>

        <button
          className="bottom-nav-profile-button"
          onClick={() => {
            if (!token) {
              setShowLoginModal(true);
            } else {
              setShowDropdown((v) => !v);
            }
          }}
          aria-label="Μενού προφίλ"
        >
          {!user ? (
            <span className="bottom-nav-profile-icon">👤</span>
          ) : user?.profile?.avatar ? (
            <img
              src={`${user.profile.avatar}?${new Date().getTime()}`}
              alt="profile"
              className="bottom-nav-profile-avatar"
            />
          ) : (
            <span
              className="bottom-nav-profile-placeholder"
              style={{ backgroundColor: getColorFromName(displayName || "") }}
            >
              {displayInitial}
            </span>
          )}
        </button>
              </div>

      {showLoginModal && (
        <Modal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)}>
          <LoginModal
            onClose={() => setShowLoginModal(false)}
            setShowLoginModal={setShowLoginModal}
            setUser={setUser}
            setToken={setToken}
            user={user}
          />
        </Modal>
      )}
      {user && showDropdown && isMobileView && (
        <div
          className="mobile-user-menu-overlay"
          onClick={closeUserMenu}
        >
          <div
            className="mobile-user-menu-sheet"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mobile-user-menu-handle" />

            <UserMenu
              onClose={closeUserMenu}
              onOpenMyAds={openMyAds}
              onOpenProfile={openProfile}
              onChangeAvatar={handleAvatarChange}
              onLogout={handleLogout}
              avatarInputId="avatar-upload-mobile"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default MainLayout;